import * as yup from 'yup';

/**
 * Shared Personal Information
 * @const {Object}
 */
const sharedPersonalInfoSchema = yup.object().shape({
    first_name: yup.string().required('Enter your first name'),
    last_name: yup.string().required('Enter your last name'),
    date_of_birth: yup.object().shape({
        dd: yup
            .string()
            .required('Please enter a valid date')
            .matches(/^[0-2]?[0-9]$|[3]?[0-1]$/, 'Please enter a valid date'),
        mm: yup.string().required('Please enter a valid date'),
        yy: yup
            .string()
            .required('Please enter both a month and year')
            .min(4, 'Minimum 4')
            .max(4, 'Maximum 4')
            .matches(
                /^(19[0-9]\d|20[0-1]\d|2016)$/,
                'Please enter a valid date'
            )
    }),
    privacy_policy_agreement_date: yup
        .date()
        .required(
            'Please check the box to confirm that you agree to the terms and conditions'
        )
        .typeError(
            'Please check the box to confirm that you agree to the terms and conditions'
        ),
    phone: yup.array().of(
        yup.object().shape({
            type: yup.string(),
            number: yup.string().when('type', {
                is: 'main',
                then: () =>
                    yup
                        .string()
                        .transform(function (currentValue) {
                            return currentValue.replace(/\(|\)|\s+|-/g, '');
                        })
                        .matches(
                            /^(?:(?:(?:00\s?|\+)44\s?|0)(?:[123678]\d{8,9}))$/,
                            'Oops. Please enter a valid phone number'
                        )
                        .required('Please enter your main contact number'),
                otherwise: () =>
                    yup
                        .string('Oops. Please enter a valid phone number')
                        .transform(function (currentValue) {
                            return currentValue.replace(/\(|\)|\s+|-/g, '');
                        })
                        .matches(
                            /^$|^(?:(?:(?:00\s?|\+)44\s?|0)(?:[123678]\d{8,9}))$/,
                            'Oops. Please enter a valid phone number'
                        )
            })
        })
    ),
    addresses: yup.array().of(
        yup.object().shape({
            residential_status: yup.string(),
            auto: yup.boolean(),
            street_line_1: yup.string(),
            town: yup.string(),
            postcode: yup.string(),
            country: yup.string(),
            equifax_token: yup.string()
        })
    )
});

/**
 * Minimum Data Requirement Personal Information - Limited Company
 * @const {Object}
 */
const minDataRequirementPersonalInfoSchema = sharedPersonalInfoSchema.shape({
    roles: yup.array().required(),
    email: yup.string().required(),
    uid: yup.string().required()
});

/**
 * Minimum Data Requirement Personal Information - Sole Trader
 * @const {Object}
 */
const minDataRequirementPersonalInfoSoleTraderSchema =
    minDataRequirementPersonalInfoSchema.shape({
        monthly_other_household_income: yup
            .number()
            .required()
            .when('number_of_dependents', {
                is: (number_of_dependents) => {
                    return number_of_dependents > 0;
                },
                then: () =>
                    yup
                        .number()
                        .required('Please enter an amount')
                        .typeError('Please enter an amount'),
                otherwise: () => yup.number()
            }),
        number_of_dependents: yup.number().required('Please select an option'),
        other_monthly_expenses: yup
            .number()
            .required()
            .typeError('Please enter an amount')
    });

/**
 * Personal Information fields schema
 * @export {Object}
 */
export const signUpLimitedCompanyPersonalInformationSchema =
    sharedPersonalInfoSchema.shape({
        title: yup.string(),
        roles: yup.array(),
        addresses: yup.array().of(
            yup.lazy((value) => {
                const isMainAddress = !value['extra_address'];
                if (isMainAddress) {
                    return yup.object().shape({
                        residential_status: validateResidentialStatus,
                        auto: yup.boolean(),
                        street_line_1: yup.string().when('auto', {
                            is: true,
                            then: () => yup.string(),
                            otherwise: () =>
                                yup.string().required('Enter address line 1')
                        }),
                        town: yup.string().when('auto', {
                            is: true,
                            then: () => yup.string(),
                            otherwise: () =>
                                yup.string().required('Enter your town')
                        }),
                        postcode: yup.string().when('auto', {
                            is: true,
                            then: () => yup.string(),
                            otherwise: () =>
                                yup.string().required('Enter your postcode')
                        }),
                        country: yup.string().when('auto', {
                            is: true,
                            then: () => yup.string(),
                            otherwise: () =>
                                yup
                                    .string()
                                    .required('You need to be a UK resident')
                                    .matches(
                                        /^uk$/,
                                        'You need to be a UK resident'
                                    )
                        }),
                        equifax_token: yup.string().when('auto', {
                            is: true,
                            then: () =>
                                yup.string().required('Enter your postcode'),
                            otherwise: () => yup.string()
                        })
                    });
                } else {
                    return yup.object().shape({
                        residential_status: yup.string(),
                        auto: yup.boolean(),
                        street_line_1: yup.string(),
                        town: yup.string(),
                        postcode: yup.string(),
                        country: yup.string(),
                        equifax_token: yup.string()
                    });
                }
            })
        )
    });

export const signUpSoleTraderPersonalInformationSchema =
    sharedPersonalInfoSchema.shape({
        title: yup.string().required('Select your title'),
        addresses: yup.array().of(
            yup.lazy((value) => {
                const isMainAddress = !value['extra_address'];
                if (isMainAddress) {
                    return yup.object().shape({
                        residential_status: yup
                            .string()
                            .required('Please make a selection'),
                        monthly_expenses: yup
                            .string()
                            .required('Please enter an amount'),
                        auto: yup.boolean(),
                        street_line_1: yup.string().when('auto', {
                            is: true,
                            then: () => yup.string(),
                            otherwise: () =>
                                yup.string().required('Enter address line 1')
                        }),
                        town: yup.string().when('auto', {
                            is: true,
                            then: () => yup.string(),
                            otherwise: () =>
                                yup.string().required('Enter your town')
                        }),
                        postcode: yup.string().when('auto', {
                            is: true,
                            then: () => yup.string(),
                            otherwise: () =>
                                yup.string().required('Enter your postcode')
                        }),
                        country: yup.string().when('auto', {
                            is: true,
                            then: () => yup.string(),
                            otherwise: () =>
                                yup
                                    .string()
                                    .required('You need to be a UK resident')
                                    .matches(
                                        /^uk$/,
                                        'You need to be a UK resident'
                                    )
                        }),
                        equifax_token: yup.string().when('auto', {
                            is: true,
                            then: () =>
                                yup.string().required('Enter your postcode'),
                            otherwise: () => yup.string()
                        })
                    });
                } else {
                    return yup.object().shape({
                        residential_status: yup.string(),
                        monthly_expenses: yup.string(),
                        street_line_1: yup.string(),
                        town: yup.string(),
                        postcode: yup.string(),
                        country: yup.string(),
                        equifax_token: yup.string()
                    });
                }
            })
        ),
        monthly_non_business_income: yup
            .number()
            .required('Please enter an amount')
            .typeError('Please enter an amount'),
        number_of_dependents: yup.number().required('Please select an option'),
        monthly_other_household_income: yup
            .number()
            .when('number_of_dependents', {
                is: (number_of_dependents) => {
                    return number_of_dependents > 0;
                },
                then: () =>
                    yup
                        .number()
                        .required('Please enter an amount')
                        .typeError('Please enter an amount'),
                otherwise: () => yup.number()
            }),
        other_monthly_expenses: yup.number().typeError('Please enter an amount')
    });

export const guarantorPersonalInformationSchema =
    sharedPersonalInfoSchema.shape({
        email: yup.string(),
        addresses: yup.array().of(
            yup.object().shape({
                auto: yup.boolean(),
                street_line_1: yup.string().when('auto', {
                    is: true,
                    then: () => yup.string(),
                    otherwise: () =>
                        yup.string().required('Enter address line 1')
                }),
                town: yup.string().when('auto', {
                    is: true,
                    then: () => yup.string(),
                    otherwise: () => yup.string().required('Enter your town')
                }),
                postcode: yup.string().when('auto', {
                    is: true,
                    then: () => yup.string(),
                    otherwise: () =>
                        yup.string().required('Enter your postcode')
                }),
                country: yup.string().when('auto', {
                    is: true,
                    then: () => yup.string(),
                    otherwise: () =>
                        yup.string().required('You need to be a UK resident')
                }),
                equifax_token: yup.string().when('auto', {
                    is: true,
                    then: () => yup.string().required('Enter your postcode'),
                    otherwise: () => yup.string()
                })
            })
        )
    });

const validateResidentialStatus = yup.lazy(
    (residentialStatus, { isInIndicativeOfferFlow }) => {
        if (!isInIndicativeOfferFlow) {
            return yup.string().required('Please make a selection');
        } else {
            return yup.string().nullable(true);
        }
    }
);

export {
    minDataRequirementPersonalInfoSoleTraderSchema as minDataReqSt,
    minDataRequirementPersonalInfoSchema as minDataReqLtd,
    signUpSoleTraderPersonalInformationSchema as st,
    signUpLimitedCompanyPersonalInformationSchema as ltd
};
